<template>
	<div class="content-page content-page--beta-features-history">
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">
					Beta Feature History
					<span class="content-page__title--detail" v-if="pageTitle"
						>- {{ pageTitle }}</span
					>
				</h2>
			</div>
		</div>

		<div class="results-toolbar results-toolbar--variant-three">
			<HistoryFilters
				v-model="filters"
				:defaultFilters="defaultFilters"
				:userOptions="users || []"
				placeholder="Search by feature name"
			/>

			<div class="results-toolbar__counter" v-if="resultCounter">
				<!--DEV NOTE: Component is pending development's implemention.-->
				<ResultCounter :counter="resultCounter" />
			</div>
		</div>

		<!--DEV NOTE: Component is pending development's implemention.-->
		<Table
			:sourceObject="tableSource"
			@change="updatedPagination"
			@sort="updateDirection"
			v-if="hasResults"
			:isArrowUp="true"
			:sortOptions="handleSort"
			class="table-container--beta-features-history"
		>
			<template #col-group>
				<colgroup>
					<!-- Change Date-->
					<col class="table__col table__col--date" />

					<!-- Feature -->
					<col class="table__col table__col--feature" />

					<!-- Customer List -->
					<col class="table__col table__col--customer" />

					<!-- Changed By -->
					<col class="table__col table__col--changed-by" />
				</colgroup>
			</template>
			<template #body-timestamp="{ body }">
				<div class="beta-history-data">
					{{ fullDateString(body.timestamp) }}

					<div class="beta-history-data__supporting-txt">
						{{ differenceDateString(body.timestamp) }}
					</div>
				</div>
			</template>
			<template #body-customerList="{ body }">
				<div class="beta-features-history__change-customers">
					<template v-if="body.customerList">
						{{ body.customerList }}
					</template>

					<FeaturesText
						v-if="body.removedItems"
						:spanLabel="'Removed Clients:'"
						:featuresText="body.removedItems"
						@click="displayModalClick"
					/>

					<FeaturesText
						v-if="body.addedItems"
						:spanLabel="'Added Clients:'"
						:featuresText="body.addedItems"
						@click="displayModalClick"
					/>
				</div>
			</template>
		</Table>
		<Spinner :processor="{ loading: loading }" />

		<NoResults v-if="!hasResults && !loading" />

		<CustomersModal
			v-model="dataDisplay"
			:source="dataCustomers"
			:renderSource="true"
		/>
	</div>
</template>

<script>
import FeaturesText from './features-text.vue';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import CustomersModal from '@/sections/shared/customers-modal';
import NoResults from '@/site-components/no-results/no-results';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Spinner from '@/components/visual/spinner/spinner';
import Table from '@/components/shells/table/table';
import headerOptions from './header-options';
import { mapActions, mapGetters, mapState } from 'vuex';
import CustomerOptionsMixins from '@/mixins/customers/customer-options';
import HistoryFilters from '@/pages/global-history-screen-filters';
import DatetimeMixin from '@/mixins/datetime-mixin';
// libraries
import { formatDate } from '@/utils/datetime';
import { sub } from 'date-fns';

//constants
import {
	BETA_FEATURES,
	MODULE_NAME,
	SET_BETA_FEATURES_ALL,
	BETA_FEATURES_ALL
} from '../beta-features-mutation-types';
import {
	MODULE_NAME as OPTION_MODULE_NAME,
	LOGS,
	SET_LOGS,
	SET_USERS_LOGS
} from '@/sections/logs/logs-mutation-type';
import {
	GET_CUSTOMER_OPTION_NAME,
	GET_CUSTOMER_OPTION_IDS
} from '@/sections/options/options-mutation-type';
import _ from 'lodash';

import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues
} from '@/utils/object';

export default {
	name: 'BetaFeaturesLogs',
	mixins: [CustomerOptionsMixins, DatetimeMixin],
	components: {
		FeaturesText,
		HistoryFilters,
		Breadcrumbs,
		CustomersModal,
		ResultCounter,
		NoResults,
		Spinner,
		Table
	},
	props: {
		orderBy: String,
		orderDirection: String,
		search: String,
		minDatetime: String,
		maxDatetime: String,
		changedByUserId: String,
		SourceApplication: String,
		changedEntityId: [String, Number],
		recordStart: Number,
		maxCount: Number
	},
	data() {
		return {
			filters: {},
			defaultFilters: {
				orderBy: 'timestamp',
				orderDirection: 'desc',
				search: undefined,
				minDatetime: formatDate(
					new Date(sub(new Date(), { weeks: 1 }))
				),
				maxDatetime: formatDate(new Date()),
				changedByUserId: undefined,
				SourceApplication: ['VueSupport'],
				entityType: ['FeaturesSwitchesModel'],
				changedEntityId: this.changedEntityId,
				recordStart: 1,
				maxCount: 10
			},
			tableSource: {
				headers: [],
				displayHeader: true,
				source: {}
			},
			loading: false,
			dataCustomers: [],
			dataDisplay: false,
			defaultUrlNames: [
				{ url: '/features', name: 'Beta Feature Manager' },
				{ name: 'History' }
			]
		};
	},
	async created() {
		this.tableSource.headers = headerOptions;
		try {
			let params = {
				entityType: 'FeaturesSwitchesModel'
			};
			if (this.changedEntityId) {
				Object.assign(params, {
					changedEntityId: this.changedEntityId
				});
			}
			await this.getUsers(params);
		} finally {
			this.loading = false;
		}
	},
	computed: {
		...mapGetters(OPTION_MODULE_NAME, [LOGS]),
		...mapGetters(MODULE_NAME, [BETA_FEATURES, BETA_FEATURES_ALL]),
		...mapState('historyLogs', ['users']),

		resultCounter() {
			return this.tableSource.source.totalCount;
		},
		hasResults() {
			return this.resultCounter > 0;
		},
		handleSort() {
			return {
				currentSort: this.filters.orderBy,
				currentSortDir: this.filters.orderDirection
			};
		},
		pageTitle() {
			return this[LOGS].pageTitle || '';
		},
		urlNames() {
			const routeBreadcrumb = this.$route.meta.breadcrumb;
			const storeBreadcrumb = this.$store.getters.breadcrumbs(
				this.$route.path
			);

			if (!_.isEmpty(routeBreadcrumb)) {
				return routeBreadcrumb;
			}

			if (!_.isEmpty(storeBreadcrumb)) {
				return storeBreadcrumb;
			}

			return this.defaultUrlNames;
		}
	},
	methods: {
		...mapActions(OPTION_MODULE_NAME, [SET_LOGS, SET_USERS_LOGS]),
		...mapActions(MODULE_NAME, [SET_BETA_FEATURES_ALL]),
		...mapActions('historyLogs', ['getUsers']),

		async fetchDataLogs() {
			this.loading = true;

			const getCustomerOptionIds = result => {
				if (!result || !result.length) return [];
				return this[GET_CUSTOMER_OPTION_IDS](result);
			};

			let filteredQuery = removeObjectEmptyValues(this.filters);

			if (filteredQuery.search) {
				const listOfCustomerIds = getCustomerOptionIds(
					filteredQuery.search
				);
				if (listOfCustomerIds.length > 0) {
					filteredQuery.customerIds = listOfCustomerIds;
				}
			}

			try {
				await this[SET_LOGS](filteredQuery);
			} finally {
				this.loading = false;
			}
		},
		async renderLogs() {
			const customerNames = result => {
				if (!result || !result.length) return [];
				return this[GET_CUSTOMER_OPTION_NAME](result);
			};
			let results = _.cloneDeep(this[LOGS]);
			if (results.items.length > 0) {
				results.items.forEach(item => {
					item.description = _.get(
						item,
						'data.items[0].Description',
						''
					);

					if (item?.fieldChanges?.items?.length > 0) {
						if (
							item?.fieldChanges?.items[0]
								?.ChangedPropertyName === 'Released'
						) {
							item.customerList = 'All Clients';
						} else {
							const removedItems = item.fieldChanges.items
								.filter(obj => obj.Action === 'Delete')
								.map(obj => obj.OriginalValue);

							const addedItems = item.fieldChanges.items
								.filter(obj => obj.Action === 'Add')
								.map(obj => obj.NewValue);

							if (removedItems) {
								item.removedItems = customerNames(
									removedItems
								).join(', ');
							}
							if (addedItems) {
								item.addedItems = customerNames(
									addedItems
								).join(', ');
							}
						}
					}
				});
			}
			this.tableSource.source = { ...results };
		},
		displayModalClick(featureText) {
			const customerNames = featureText.split(',');
			let items = [...new Set(customerNames)].map(cn => {
				return {
					text: cn,
					value: cn
				};
			});
			this.dataCustomers = items;
			this.dataDisplay = true;
		},
		updateRoute(filters) {
			let queries = removePropertiesWithSameValues(
				filters,
				this.defaultFilters
			);

			queries = removeObjectEmptyValues(queries);

			let pathQuery = ['/features', 'history'];
			if (this.changedEntityId) {
				pathQuery.splice(1, 0, this.changedEntityId);
			}
			const path = pathQuery.join('/');

			this.$router
				.push({
					path: path,
					query: queries
				})
				.catch(() => {});
		},
		updateDirection(sortValue) {
			this.filters.orderDirection = sortValue.sortDir;
			this.filters.orderBy = sortValue.sortBy;
		},
		updatedPagination(value) {
			this.filters.recordStart = value.page;
			this.filters.maxCount = value.countPerPage;
		}
	},
	watch: {
		filters: {
			deep: true,
			async handler(newquery) {
				this.updateRoute(newquery);
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				if (newQuery.maxCount) {
					const availablemaxCount = [10, 20, 30, 50, 100];

					if (availablemaxCount.includes(this.maxCount)) {
						newQuery.maxCount = this.maxCount;
					} else {
						newQuery.maxCount = this.defaultFilters.maxCount;
					}
				}

				this.filters = { ...this.defaultFilters, ...newQuery };
				this.fetchDataLogs();
			}
		},
		[LOGS]: {
			deep: true,
			handler(val) {
				if (!_.isEmpty(val)) {
					this.renderLogs();
				}
			}
		}
	}
};
</script>

<style lang="scss">
/**
  * Beta Feature History
*/

/*------------------------------------*\
  Result Filter - BASE
\*------------------------------------*/

.filter {
	display: flex;
	align-items: center;

	&__fields,
	&__summary {
		display: none;
	}

	.modal__body &__fields--modal {
		.beta-feature-history {
			width: 100%;
			display: flex;
			flex-direction: column;
			gap: $gutter-lg;

			.dropdown {
				width: 100%;
				margin: 0;
			}
		}
	}

	&__actions {
		display: flex;
		gap: $gutter-lg;

		.filter__reset-btn {
			margin-left: 0;
			margin-right: 0;
		}

		.btn__verbose {
			display: block;
		}

		// fix layout on mobiles with less than 360px ( Samsung Galaxy S4, Iphone 5, etc )
		@include mq($until: sm-l) {
			.filter__trigger {
				min-width: 44px;
			}
		}

		.filter__trigger .icon {
			margin-right: 0;
		}
	}
}

/*------------------------------------*\
  Result Filter - BASE TABLET-SMALL
\*------------------------------------*/

@include mq($from: sm-l) {
	.filter {
		&__actions {
			margin-left: 0;
			margin-right: $gutter-lg;
		}

		.modal {
			width: auto;
			height: auto;
			max-height: 80vh;
		}
	}
}

@include mq($from: md) {
	.filter {
		.modal {
			&__body {
				.beta-feature-history {
					flex-direction: initial;
					align-items: center;

					.dropdown {
						width: 170px;
						margin-left: $gutter-lg;
					}
				}
			}
		}

		&__actions {
			flex-direction: row-reverse;
			margin-right: 0;
			gap: $gutter-lg;

			.filter__trigger .icon {
				margin-right: $gutter-md;
			}

			.filter__reset-btn {
				order: 1;
			}
		}

		&__summary {
			display: inline;
			font-size: $filter-font-size;
			color: $neutral-2;
			@include ellipsis;
		}

		&__summary-counter {
			font-weight: $bold-font-weight;
			margin-right: $gutter-md;
		}
	}
}

/*------------------------------------*\
  Result Filter - BASE DESKTOP
\*------------------------------------*/

@include mq($from: md-l) {
	.filter {
		gap: $gutter-md;
		flex-direction: row-reverse;

		&__fields {
			display: flex;
		}

		.beta-feature-history {
			display: flex;
			gap: $gutter-md;
		}

		&__summary {
			display: none;
		}

		&__actions {
			.filter__trigger {
				display: none;
			}
		}
	}
}

/*------------------------------------*\
  Beta Feature History Table
\*------------------------------------*/

.table-container--beta-features-history {
	.table {
		@include mq($from: md) {
			table-layout: fixed;
		}
	}
	.table__col {
		width: 100%;

		@include mq($from: md) {
			&--date {
				width: 105px;
			}
			&--feature,
			&--customer {
				width: 122px;
			}
			&--changed-by {
				width: 85px;
			}
		}

		@include mq($from: md-l) {
			&--date {
				width: 85px;
			}
			&--feature,
			&--customer {
				width: 150px;
			}
			&--changed-by {
				width: 75px;
			}
		}
	}
}

/*------------------------------------*\
  Beta Feature History Table - Change Date
\*------------------------------------*/

.beta-history-data {
	&__supporting-txt {
		color: $neutral-2;
		margin-top: $gutter-sm;
	}
}
</style>

<style lang="scss" scoped>
.results-toolbar {
	width: 100%;
}
::v-deep .results-toolbar__search {
	@include mq($from: md) {
		width: 220px;
	}
}
</style>
